// The portrait crop right after a photo is picked (the photo may show several people):
// drag the photo, zoom with the slider, the mouse wheel or two fingers, until head (with
// all the hair) and shoulders sit in the portrait frame (3:4, Dennis: the old face circle
// was too small and the clothes count too). The frame becomes a JPEG (longest side 1024 at
// most) and REPLACES the file in the form - the server never sees the original.
// HDR photos (PQ/HLG profile, iPhone/macOS 26 exports) are tone-mapped first (hdr.js), or
// Chrome would show - and the crop would keep - a flat grey picture.
//
//   const file = await cropPhoto(originalFile, {frame})
//     -> a File (image/jpeg) carrying .frame ({x, y, w, h}: the chosen frame as fractions of the photo),
//        .source (the photo as shown - the SDR copy of an HDR one) and .ratio (its width / height), for the
//        preview (photopick.js: the whole photo with the frame drawn on it);
//        null (cancelled), {other: true} ("Anderes Foto") or {error} (not an image).
//   `frame` (optional): start where the last crop of this photo was ("Neu zuschneiden").
//
// Plain JS, no library; the dialog is built here, styled in scss/_wizard.scss (.crop-*).
const {toSdr} = require('./hdr');

const OUT = 1024;               // the crop's longest side in pixels at most (the anchor painter needs no more)
const RATIO = 3 / 4;            // the frame: width / height
const FRAME = 0.84;             // the frame's width in the stage; around it a veil shows what is cut off
let dialog = null;

function build() {
  dialog = document.createElement('dialog');
  dialog.className = 'sheet crop-dialog';
  dialog.innerHTML = `<div class="p-5 sm:p-7">
    <div class="flex items-start justify-between gap-4">
      <h2 class="text-2xl font-semibold">Wer ist es? Kopf und Oberkörper in den Rahmen</h2>
      <button type="button" class="btn-ghost -mr-2 -mt-1 text-xl" data-crop-cancel aria-label="Abbrechen">✕</button>
    </div>
    <p class="mt-2 text-ink-soft">Schieb das Foto, bis Kopf und Oberkörper im Rahmen sind – mit allen Haaren und der Kleidung. Sind mehrere Menschen drauf? Dann einfach heranzoomen.</p>
    <div class="crop-stage mt-4" data-crop-stage tabindex="0" aria-label="Foto verschieben (Pfeiltasten) und zoomen (+/-)">
      <img alt="" data-crop-img draggable="false">
      <span class="crop-frame" aria-hidden="true" data-crop-frame></span>
    </div>
    <label class="mt-4 flex items-center gap-3 text-sm font-semibold text-night">
      <span aria-hidden="true">➖</span>
      <input type="range" min="1" max="5" step="0.01" value="1" class="crop-zoom flex-1" data-crop-zoom aria-label="Zoom">
      <span aria-hidden="true">➕</span>
    </label>
    <div class="mt-5 flex flex-wrap justify-end gap-2">
      <button type="button" class="btn-secondary" data-crop-other>Anderes Foto</button>
      <button type="button" class="btn-primary" data-crop-ok>✓ So passt es</button>
    </div>
  </div>`;
  document.body.appendChild(dialog);
}

function cropPhoto(file, {frame = null} = {}) {
  if (!dialog) build();
  return new Promise(async (resolve) => {
    const stage = dialog.querySelector('[data-crop-stage]');
    const frameEl = dialog.querySelector('[data-crop-frame]');
    const img = dialog.querySelector('[data-crop-img]');
    const zoomInput = dialog.querySelector('[data-crop-zoom]');
    const sdr = await toSdr(file);                               // null: the browser shows it right
    const url = URL.createObjectURL(sdr || file);
    // stage size; the frame (fx, fy, fw, fh) inside it; x/y: image top-left in stage px
    let w = 0, h = 0, sw = 0, sh = 0, fx = 0, fy = 0, fw = 0, fh = 0, base = 1, zoom = 1, x = 0, y = 0;
    const pointers = new Map();
    let pinch = null;

    const scale = () => base * zoom;
    function clamp() {                                           // the photo always covers the frame
      const s = scale();
      x = Math.min(fx, Math.max(fx + fw - w * s, x));
      y = Math.min(fy, Math.max(fy + fh - h * s, y));
    }
    function draw() {
      clamp();
      img.style.width = `${w * scale()}px`;
      img.style.height = `${h * scale()}px`;
      img.style.transform = `translate(${x}px, ${y}px)`;
      zoomInput.value = zoom;
    }
    // zoom around a point of the stage (cx, cy), so what is under the finger stays there
    function zoomTo(next, cx = fx + fw / 2, cy = fy + fh / 2) {
      next = Math.max(1, Math.min(5, next));
      const before = scale();
      zoom = next;
      const after = scale();
      x = cx - (cx - x) * (after / before);
      y = cy - (cy - y) * (after / before);
      draw();
    }

    function onDown(e) {
      stage.setPointerCapture(e.pointerId);
      pointers.set(e.pointerId, {x: e.clientX, y: e.clientY});
      if (pointers.size === 2) {
        const [a, b] = [...pointers.values()];
        pinch = {d: Math.hypot(a.x - b.x, a.y - b.y), zoom};
      }
    }
    function onMove(e) {
      if (!pointers.has(e.pointerId)) return;
      const last = pointers.get(e.pointerId);
      const now = {x: e.clientX, y: e.clientY};
      pointers.set(e.pointerId, now);
      if (pointers.size === 1) {
        x += now.x - last.x;
        y += now.y - last.y;
        draw();
      } else if (pointers.size === 2 && pinch) {
        const [a, b] = [...pointers.values()];
        const rect = stage.getBoundingClientRect();
        zoomTo(pinch.zoom * (Math.hypot(a.x - b.x, a.y - b.y) / pinch.d), (a.x + b.x) / 2 - rect.left, (a.y + b.y) / 2 - rect.top);
      }
    }
    function onUp(e) {
      pointers.delete(e.pointerId);
      if (pointers.size < 2) pinch = null;
    }
    function onWheel(e) {
      e.preventDefault();
      const rect = stage.getBoundingClientRect();
      zoomTo(zoom * (e.deltaY < 0 ? 1.08 : 1 / 1.08), e.clientX - rect.left, e.clientY - rect.top);
    }
    function onKey(e) {
      const step = 12;
      const moves = {ArrowLeft: [step, 0], ArrowRight: [-step, 0], ArrowUp: [0, step], ArrowDown: [0, -step]};
      if (moves[e.key]) { e.preventDefault(); x += moves[e.key][0]; y += moves[e.key][1]; draw(); }
      if (e.key === '+' || e.key === '=') zoomTo(zoom * 1.1);
      if (e.key === '-') zoomTo(zoom / 1.1);
    }
    const onZoom = () => zoomTo(Number(zoomInput.value));

    function finish(result) {
      stage.removeEventListener('pointerdown', onDown);
      stage.removeEventListener('pointermove', onMove);
      stage.removeEventListener('pointerup', onUp);
      stage.removeEventListener('pointercancel', onUp);
      stage.removeEventListener('wheel', onWheel);
      stage.removeEventListener('keydown', onKey);
      zoomInput.removeEventListener('input', onZoom);
      dialog.querySelectorAll('[data-crop-cancel]').forEach(b => { b.onclick = null; });
      dialog.querySelector('[data-crop-other]').onclick = null;
      dialog.querySelector('[data-crop-ok]').onclick = null;
      dialog.onclose = null;
      if (dialog.open) dialog.close();
      if (!(result instanceof File)) URL.revokeObjectURL(url);     // a crop keeps it: its preview shows the photo
      resolve(result);
    }

    function crop() {
      const s = scale();
      const srcW = fw / s, srcH = fh / s;                         // the frame in original pixels
      const outH = Math.round(Math.min(OUT, srcH));
      const outW = Math.round(outH * RATIO);
      const canvas = document.createElement('canvas');
      canvas.width = outW;
      canvas.height = outH;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, outW, outH);
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, (fx - x) / s, (fy - y) / s, srcW, srcH, 0, 0, outW, outH);
      canvas.toBlob((blob) => {
        if (!blob) { finish(null); return; }
        const name = (file.name || 'foto').replace(/\.[^.]+$/, '') + '-portrait.jpg';
        const out = new File([blob], name, {type: 'image/jpeg'});
        out.frame = {x: (fx - x) / s / w, y: (fy - y) / s / h, w: srcW / w, h: srcH / h};
        out.source = sdr || file;
        out.sourceUrl = url;
        out.ratio = w / h;
        finish(out);
      }, 'image/jpeg', 0.9);
    }

    img.onload = () => {
      w = img.naturalWidth;
      h = img.naturalHeight;
      dialog.showModal();
      sw = stage.clientWidth;
      sh = stage.clientHeight;
      fw = sw * FRAME;
      fh = fw / RATIO;
      if (fh > sh * 0.94) { fh = sh * 0.94; fw = fh * RATIO; }
      fx = (sw - fw) / 2;
      fy = (sh - fh) / 2;
      Object.assign(frameEl.style, {left: `${fx}px`, top: `${fy}px`, width: `${fw}px`, height: `${fh}px`});
      base = Math.max(fw / w, fh / h);                           // the photo covers the frame at zoom 1
      zoom = 1;                                                  // a selfie: the whole picture fits
      // centred; people are usually in the upper part of a photo
      x = fx + (fw - w * base) / 2;
      y = fy + (fh - h * base) * 0.3;
      if (frame && frame.w > 0) {                                // again: where the last crop was
        zoom = Math.max(1, Math.min(5, fw / (frame.w * w) / base));
        x = fx - frame.x * w * scale();
        y = fy - frame.y * h * scale();
      }
      draw();
      stage.focus({preventScroll: true});
    };
    img.onerror = () => {                 // a broken or unknown file: the caller says so next to the photo
      URL.revokeObjectURL(url);
      resolve({error: 'Dieses Foto lässt sich leider nicht öffnen. Bitte wähle ein anderes Bild (JPG, PNG oder WebP).'});
    };
    stage.addEventListener('pointerdown', onDown);
    stage.addEventListener('pointermove', onMove);
    stage.addEventListener('pointerup', onUp);
    stage.addEventListener('pointercancel', onUp);
    stage.addEventListener('wheel', onWheel, {passive: false});
    stage.addEventListener('keydown', onKey);
    zoomInput.addEventListener('input', onZoom);
    dialog.querySelectorAll('[data-crop-cancel]').forEach(b => { b.onclick = () => finish(null); });
    dialog.querySelector('[data-crop-other]').onclick = () => finish({other: true});
    dialog.querySelector('[data-crop-ok]').onclick = crop;
    dialog.onclose = () => finish(null);
    img.src = url;
  });
}

module.exports = {cropPhoto};
